import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import Select from '../../Select'
import SelectPaginated from '../../SelectPaginated'

function SelectField(props) {
  const {
    setFieldValue,
    setFieldTouched,
    isMulti,
    isClearable,
    name,
    value,
    options,
    preselectFirstOptionValue,
    onValueChange,
    setFullOptionObject
  } = props

  const selectFieldProps = useMemo(
    () => ({
      ...props,
      formatValue: !setFullOptionObject
    }),
    [props, setFullOptionObject]
  )

  const handleChange = useCallback(
    (value, selectData) => {
      const newValue = setFullOptionObject ? value : !!value ? value.value : undefined

      setFieldValue(selectData.name, newValue)
      setFieldTouched(selectData.name, true, false)
      // callback to handle some actions from outside
      onValueChange && onValueChange(newValue, value)
    },
    [setFullOptionObject, setFieldValue, setFieldTouched, onValueChange]
  )

  const handleMultiChange = useCallback(
    (value, selectData) => {
      setFieldValue(selectData.name, value || [])
      setFieldTouched(selectData.name, true, false)
    },
    [setFieldValue, setFieldTouched]
  )

  const handleClear = useCallback(() => {
    setFieldValue(name, setFullOptionObject ? undefined : '')
  }, [setFieldValue, name, setFullOptionObject])

  // If preselectFirstOptionValue prop is passed to the component
  // this useEffect automatically preselects first option from the options list
  useEffect(() => {
    if (preselectFirstOptionValue && options.length && !value) {
      if (setFullOptionObject) {
        setFieldValue(name, options[0])
      } else {
        setFieldValue(name, options[0].value)
      }
    }
  }, [preselectFirstOptionValue, options, name, value, setFieldValue, setFullOptionObject])

  if (props.loadOptions) {
    // don't pass isLoading prop to SelectPaginated as it reset internal loading management
    const { isLoading, ...restProps } = { ...selectFieldProps }

    return (
      <SelectPaginated
        {...restProps}
        onChange={isMulti ? handleMultiChange : handleChange}
        onClearInput={handleClear}
        showClearInputIcon={isClearable && !!value}
      />
    )
  }

  return (
    <Select
      {...selectFieldProps}
      onChange={isMulti ? handleMultiChange : handleChange}
      onClearInput={handleClear}
      showClearInputIcon={isClearable && !!value}
    />
  )
}

export default SelectField

SelectField.propTypes = {
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  // isClearable is custom option which controls showing ClearInputIcon and doesn't relates to original isClearable rect-select prop
  isClearable: PropTypes.bool,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  preselectFirstOptionValue: PropTypes.bool
}
